/**
 * BatchAccessGate — Iter 191 · rewritten Iter 193.
 *
 * Wraps any child route that belongs to a specific batch and enforces
 * BOTH batch-level gates:
 *   1. Admin restriction (via `/me/batches/{id}/access`) — shows
 *      <BatchRestrictedScreen /> when the student was surgically
 *      restricted from this batch.
 *   2. Streak check-in gate (via `/me/batches/{id}/checkin-gate`) —
 *      shows <CheckinGateScreen /> when this batch has streak enabled
 *      AND `require_checkin_gate=true` AND the student hasn't
 *      checked in yet today.
 *
 * Both checks are strictly scoped to the passed `batchId` so the
 * student's OTHER batches remain accessible even when this one is
 * locked. Fixes the Iter 192 bug where a single batch's toggle
 * globally locked every route.
 *
 * Usage:
 *   <BatchAccessGate batchId={batchId}>
 *     <StudyContent batchId={batchId} />
 *   </BatchAccessGate>
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "../contexts/AuthContext";
import BatchRestrictedScreen from "../pages/BatchRestrictedScreen";
import CheckinGateScreen from "./CheckinGateScreen";

export default function BatchAccessGate({ batchId, children }) {
  const [restrict, setRestrict] = useState({ loading: true, restricted: false });
  const [gate, setGate] = useState({ loading: true, data: null });

  const loadGate = useCallback((bid) => {
    if (!bid) { setGate({ loading: false, data: null }); return; }
    axios.get(`${API}/me/batches/${bid}/checkin-gate`)
      .then((r) => setGate({ loading: false, data: r.data }))
      .catch(() => setGate({ loading: false, data: null }));
  }, []);

  useEffect(() => {
    if (!batchId) {
      setRestrict({ loading: false, restricted: false });
      setGate({ loading: false, data: null });
      return;
    }
    let cancelled = false;
    setRestrict({ loading: true, restricted: false });
    setGate({ loading: true, data: null });
    axios.get(`${API}/me/batches/${batchId}/access`)
      .then((r) => {
        if (cancelled) return;
        setRestrict({ loading: false, restricted: !!r.data?.restricted });
      })
      .catch(() => { if (!cancelled) setRestrict({ loading: false, restricted: false }); });
    loadGate(batchId);
    return () => { cancelled = true; };
  }, [batchId, loadGate]);

  // Still resolving — render children so we don't flash the gate on nav
  if (restrict.loading || gate.loading) return children;

  if (restrict.restricted) return <BatchRestrictedScreen batchId={batchId} />;

  const g = gate.data;
  if (g?.gate_on && !g?.checked_in_today) {
    return (
      <CheckinGateScreen
        cfg={g.config}
        state={g.state}
        v2={g.v2}
        batchName={g.batch?.name}
        onRefresh={() => loadGate(batchId)}
      />
    );
  }

  return children;
}
